import React, { useState } from "react";
import { Tooltip } from "@mui/material";
import { formatINR, formatUSD, formatMonthLabel, formatFxRate } from "./revenueSummaryUtils";

const CHARGEBACK_TOOLTIP = "ProRewards chargeback recorded on invoice.";
const DB_CHARGEBACK_TOOLTIP = "Database chargeback from non-invoiced or DB-only adjustments.";
const PAYMENT_PENDING_TOOLTIP = "Outstanding payment on raised invoices.";
const PENDING_INVOICE_TOOLTIP = "Revenue earned but invoice not yet raised this month.";
const FX_RATE_TOOLTIP = "Pending USD converted at today's FX rate.";
const TODAY_FX_DEFAULT = 94;

const InfoTooltip = ({ title }) => (
	<Tooltip title={title} arrow placement="top">
		<span className="rs-info-icon" onClick={(e) => e.stopPropagation()}>i</span>
	</Tooltip>
);

const CalcRow = ({
	label,
	value,
	indent = 0,
	muted = false,
	total = false,
	grand = false,
	tooltip = null,
	sub = null,
	bracket = null,
	bracketFirst = false,
	bracketStacked = false,
}) => (
	<div
		className={[
			"rs-calc-row",
			indent ? `rs-calc-row--indent-${indent}` : "",
			muted ? "rs-calc-row--muted" : "",
			total ? "rs-calc-row--total" : "",
			grand ? "rs-calc-row--grand" : "",
		].filter(Boolean).join(" ")}
	>
		<span className="rs-calc-label">
			{label}
			{tooltip && <InfoTooltip title={tooltip} />}
		</span>
		<span
			className={[
				"rs-calc-num",
				bracketStacked ? "rs-calc-num--stacked" : "",
			].filter(Boolean).join(" ")}
		>
			{bracketFirst && bracket && (
				<span className="rs-calc-bracket rs-calc-bracket--first">({bracket})</span>
			)}
			<span className={bracketStacked ? "rs-calc-num-main" : undefined}>{value}</span>
			{!bracketFirst && bracket && <span className="rs-calc-bracket"> ({bracket})</span>}
			{sub && <span className="rs-calc-sub">{sub}</span>}
		</span>
	</div>
);

const RevenueAccordion = ({ title, summary, expanded, onToggle, children }) => (
	<div className="rs-cashflow-accordion rs-cashflow-accordion--section rs-revenue-accordion">
		<button type="button" className="rs-cashflow-accordion-header" onClick={onToggle}>
			<span className="rs-cashflow-accordion-left">
				<span className="rs-cashflow-accordion-chevron">{expanded ? "▼" : "▶"}</span>
				<span className="rs-cashflow-accordion-title">{title}</span>
			</span>
			{summary}
		</button>
		{expanded && (
			<div className="rs-cashflow-accordion-body">
				{children}
			</div>
		)}
	</div>
);

const RevenueSheetBucket = ({
	currency,
	totalRevenue,
	prorewardsAmount,
	otherAppsAmount,
	chargebacks,
	cbFromInvoices,
	cbFromDatabase,
	revenuePartnerReported,
	revenuePendingInvoice,
	finalRevenue,
	realizedInBank,
	realizedInBankBracket,
	collectionPending,
	collectionPendingBracket,
	finalAmount,
	finalAmountBracket,
	showCollectionPending = false,
}) => {
	const fmt = currency === "INR" ? formatINR : formatUSD;
	const variant = currency === "INR" ? "inr" : "usd";
	const prChargeback = cbFromInvoices ?? 0;
	const dbChargeback = cbFromDatabase ?? 0;
	const isUsd = currency === "USD";

	return (
		<div className={`rs-revenue-sheet-bucket rs-revenue-sheet-bucket--${variant}`}>
			<div className="rs-revenue-sheet-bucket-title">
				{currency === "INR" ? "INR Partners" : "USD Partners"}
			</div>

			<div className="rs-revenue-calc">
				<div className="rs-revenue-sheet-section">
					<div className="rs-revenue-sheet-section-heading">Revenue Earned</div>
					<CalcRow label="ProRewards (DB)" value={fmt(prorewardsAmount)} indent={1} muted />
					<CalcRow label="Other Apps" value={fmt(otherAppsAmount)} indent={1} muted />
					<CalcRow label="Total Revenue" value={fmt(totalRevenue)} total />
					<CalcRow
						label="− Chargebacks"
						value={fmt(prChargeback || chargebacks)}
						muted
						tooltip={CHARGEBACK_TOOLTIP}
					/>
					<CalcRow
						label="− DB Chargebacks"
						value={fmt(dbChargeback)}
						muted
						tooltip={DB_CHARGEBACK_TOOLTIP}
					/>
					<CalcRow label="Partner Reported" value={fmt(revenuePartnerReported)} indent={1} muted />
					<CalcRow
						label="Pending Invoice"
						value={fmt(revenuePendingInvoice)}
						indent={1}
						muted
						tooltip={PENDING_INVOICE_TOOLTIP}
					/>
					<CalcRow label="Final Revenue" value={fmt(finalRevenue)} grand />
				</div>

				<div className="rs-revenue-sheet-section rs-revenue-sheet-section--last">
					<div className="rs-revenue-sheet-section-heading">Collections</div>
					<CalcRow
						label="Realized"
						value={fmt(realizedInBank)}
						bracket={realizedInBankBracket}
						bracketFirst={isUsd}
						bracketStacked={isUsd}
						total
					/>
					{showCollectionPending && (
						<>
							<CalcRow
								label="Payment Pending"
								value={fmt(collectionPending)}
								bracket={collectionPendingBracket}
								bracketFirst={isUsd}
								bracketStacked={isUsd}
								muted
								tooltip={isUsd ? FX_RATE_TOOLTIP : PAYMENT_PENDING_TOOLTIP}
							/>
							<CalcRow
								label="Final Amount"
								value={fmt(finalAmount)}
								bracket={finalAmountBracket}
								bracketFirst={isUsd}
								bracketStacked={isUsd}
								grand
							/>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

const resolvePendingInvoice = (reconciliation, revenue, currency, totalRevenue, chargebacks, partnerReported) => {
	const variance = revenue?.variance || {};
	if (currency === "INR") {
		if (reconciliation.pendingInvoiceINR != null) return reconciliation.pendingInvoiceINR;
		if (variance.inr != null) return variance.inr;
	} else {
		if (reconciliation.pendingInvoiceUSD != null) return reconciliation.pendingInvoiceUSD;
		if (variance.usd != null) return variance.usd;
	}
	return Math.max(0, Number((totalRevenue - chargebacks - partnerReported).toFixed(2)));
};

const resolvePendingFxRate = (revenue) =>
	revenue?.fxRateToday ??
	revenue?.currentFxRate ??
	TODAY_FX_DEFAULT;

const RevenuePerformanceCard = ({ revenue, month, periodLabel }) => {
	const [expanded, setExpanded] = useState(false);

	if (!revenue) return null;

	const monthLabel = periodLabel || formatMonthLabel(month);

	const totalRev = revenue.totalRevenue || {};
	const prorewardsRaw = totalRev.prorewards || {};
	const reported = revenue.partnerReported || {};
	const reconciliation = revenue.reconciliation || {};
	const chargebacks = revenue.chargebacks || {};
	const cbFromInvoices = chargebacks.fromInvoices || {};
	const cbFromDatabase = chargebacks.fromDatabase || {};

	const totalRevenueINR =
		reconciliation.totalRevenueINR ?? totalRev.combined?.inr ?? revenue.totalRevenueINR ?? 0;
	const totalRevenueUSD =
		reconciliation.totalRevenueUSD ?? totalRev.combined?.usd ?? revenue.totalRevenueUSD ?? 0;

	const prorewardsINR = prorewardsRaw.inr ?? 0;
	const prorewardsUSD = prorewardsRaw.usd ?? 0;

	const otherAppsINR = reconciliation.otherAppsINR ?? 0;
	const otherAppsUSD = reconciliation.otherAppsUSD ?? 0;

	const partnerReportedINR = reconciliation.partnerReportedINR ?? reported.fromINRPartners ?? 0;
	const partnerReportedUSD = reconciliation.partnerReportedUSD ?? reported.fromUSDPartners ?? 0;
	const chargebacksINR = reconciliation.chargebacksINR ?? chargebacks.inr ?? 0;
	const chargebacksUSD = reconciliation.chargebacksUSD ?? chargebacks.usd ?? 0;

	const dbChargebackINR = cbFromDatabase.inr ?? 0;
	const dbChargebackUSD = cbFromDatabase.usd ?? 0;

	const pendingInvoiceINR = resolvePendingInvoice(
		reconciliation,
		revenue,
		"INR",
		totalRevenueINR,
		chargebacksINR,
		partnerReportedINR,
	);
	const pendingInvoiceUSD = resolvePendingInvoice(
		reconciliation,
		revenue,
		"USD",
		totalRevenueUSD,
		chargebacksUSD,
		partnerReportedUSD,
	);

	const finalRevenueINR = partnerReportedINR + pendingInvoiceINR - dbChargebackINR;
	const finalRevenueUSD = partnerReportedUSD + pendingInvoiceUSD - dbChargebackUSD;

	const realized = revenue.realized || {};
	const fromINR = realized.fromINRInvoices || {};
	const fromUSD = realized.fromUSDInvoices || {};
	const inrBreakdown = fromINR.breakdown || {};
	const usdBreakdown = fromUSD.breakdown || {};
	const pending = revenue.pending || {};
	const pendingINR = pending.fromINRInvoices || {};
	const pendingUSD = pending.fromUSDInvoices || {};

	const inrNetInBank =
		inrBreakdown.netReceivedINR ?? fromINR.netReceivedINR ?? fromINR.totalINR ?? 0;

	const usdNet = usdBreakdown.netReceivedUSD ?? 0;
	const usdNetInr = usdBreakdown.netReceivedINR ?? fromUSD.totalRealizedINR ?? 0;

	const pendingInr = pendingINR.outstandingINR ?? 0;
	const pendingUsd = pendingUSD.outstandingUSD ?? 0;

	const pendingFxRate = resolvePendingFxRate(revenue);
	const pendingUsdInr = Number((pendingUsd * pendingFxRate).toFixed(2));

	const inrFinalAmount = inrNetInBank + pendingInr;
	const usdFinalAmountUsd = usdNet + pendingUsd;
	const usdFinalAmountInr = usdNetInr + pendingUsdInr;

	// Header pills show Final Revenue (earned), not realized/collections.
	const finalRevenueUsdInr = Number((finalRevenueUSD * pendingFxRate).toFixed(2));
	const totalFinalRevenueInr = finalRevenueINR + finalRevenueUsdInr;

	const accordionTitle = monthLabel
		? `Revenue Breakdown (${monthLabel})`
		: "Revenue Breakdown";

	return (
		<RevenueAccordion
			title={accordionTitle}
			expanded={expanded}
			onToggle={() => setExpanded((prev) => !prev)}
			summary={(
				<div className="rs-cashflow-header-summary">
					<span className="rs-cashflow-header-pill rs-cashflow-header-pill--in">
						INR <span className="rs-calc-num">{formatINR(finalRevenueINR)}</span>
					</span>
					<span className="rs-cashflow-header-pill rs-cashflow-header-pill--net">
						USD
						{finalRevenueUsdInr > 0 && (
							<span className="rs-revenue-header-inr-sub">({formatINR(finalRevenueUsdInr)})</span>
						)}
						<span className="rs-calc-num">{formatUSD(finalRevenueUSD)}</span>
					</span>
					<span className="rs-cashflow-header-pill rs-cashflow-header-pill--net">
						Total <span className="rs-calc-num">{formatINR(totalFinalRevenueInr)}</span>
					</span>
				</div>
			)}
		>
			<div className="rs-revenue-sheet-grid">
				<RevenueSheetBucket
					currency="INR"
					totalRevenue={totalRevenueINR}
					prorewardsAmount={prorewardsINR}
					otherAppsAmount={otherAppsINR}
					chargebacks={chargebacksINR}
					cbFromInvoices={cbFromInvoices.inr}
					cbFromDatabase={dbChargebackINR}
					revenuePartnerReported={partnerReportedINR}
					revenuePendingInvoice={pendingInvoiceINR}
					finalRevenue={finalRevenueINR}
					realizedInBank={inrNetInBank}
					collectionPending={pendingInr}
					finalAmount={inrFinalAmount}
					showCollectionPending
				/>
				<RevenueSheetBucket
					currency="USD"
					totalRevenue={totalRevenueUSD}
					prorewardsAmount={prorewardsUSD}
					otherAppsAmount={otherAppsUSD}
					chargebacks={chargebacksUSD}
					cbFromInvoices={cbFromInvoices.usd}
					cbFromDatabase={dbChargebackUSD}
					revenuePartnerReported={partnerReportedUSD}
					revenuePendingInvoice={pendingInvoiceUSD}
					finalRevenue={finalRevenueUSD}
					realizedInBank={usdNet}
					realizedInBankBracket={usdNetInr > 0 ? formatINR(usdNetInr) : null}
					collectionPending={pendingUsd}
					collectionPendingBracket={
						pendingUsdInr > 0
							? `${formatINR(pendingUsdInr)} @ ${formatFxRate(pendingFxRate)}`
							: null
					}
					finalAmount={usdFinalAmountUsd}
					finalAmountBracket={usdFinalAmountInr > 0 ? formatINR(usdFinalAmountInr) : null}
					showCollectionPending
				/>
			</div>
		</RevenueAccordion>
	);
};

export default RevenuePerformanceCard;
